import { useEffect, useState } from 'react';
import { type FindQuery, type FlagGroup, type FlagStatusFilter, type FlagTypeDef } from '../../lib/flags';
import { FlagIcon } from './FlagIcon';

/**
 * The Find filters (#316), shared with phase 2's module editor so the two
 * cannot drift: type chips, status, project, series, person, dates, text and
 * grouping.
 */

export interface Option { id: string; name: string }

interface Props {
  value: FindQuery;
  onChange: (next: FindQuery) => void;
  types: FlagTypeDef[];
  projects: Option[];
  series: Option[];
  people: Option[];
}

const STATUSES: Array<[FlagStatusFilter, string]> = [['open', 'Open'], ['addressed', 'Addressed'], ['all', 'All']];
const GROUPS: Array<[FlagGroup, string]> = [['kind', 'Type'], ['source', 'Where'], ['none', 'Date']];

function Segmented<T extends string>({ label, options, value, onChange }: { label: string; options: Array<[T, string]>; value: T; onChange: (v: T) => void }) {
  return (
    <div role="group" aria-label={label} className="flex rounded-lg border border-border bg-bg-secondary p-0.5">
      {options.map(([v, text]) => (
        <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}
          className={`flex-1 rounded-md px-1.5 py-1 text-xs ${value === v ? 'bg-bg-hover text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>
          {text}
        </button>
      ))}
    </div>
  );
}

const heading = 'text-[11px] font-semibold uppercase tracking-wider text-text-muted';
const field = 'w-full min-w-0 rounded-md border border-border-hover bg-bg-input px-2 py-1 text-sm text-text-primary';

export function FlagFilters({ value, onChange, types, projects, series, people }: Props) {
  const set = (patch: Partial<FindQuery>) => onChange({ ...value, ...patch });
  // Text is debounced; everything else applies at once.
  const [text, setText] = useState(value.q ?? '');
  useEffect(() => { setText(value.q ?? ''); }, [value.q]);
  useEffect(() => {
    if ((value.q ?? '') === text) return;
    const t = setTimeout(() => onChange({ ...value, q: text || undefined }), 250);
    return () => clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleType = (id: string) => set({ types: value.types.includes(id) ? value.types.filter((t) => t !== id) : [...value.types, id] });

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex flex-col gap-1.5">
        <span className={heading}>Type</span>
        <div className="flex flex-wrap gap-1.5">
          {types.filter((t) => !t.archived || value.types.includes(t.id)).map((t) => {
            const on = value.types.includes(t.id);
            return (
              <button key={t.id} type="button" aria-pressed={on} onClick={() => toggleType(t.id)}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${on ? 'text-text-primary' : 'border-border-hover text-text-secondary hover:text-text-primary'}`}
                style={on ? { borderColor: t.color, background: `color-mix(in srgb, ${t.color} 16%, transparent)` } : undefined}>
                <FlagIcon def={t} size={13} />{t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className={heading}>Status</span>
        <Segmented label="Status" options={STATUSES} value={value.status} onChange={(status) => set({ status })} />
      </div>
      <label className="flex flex-col gap-1.5">
        <span className={heading}>Project</span>
        <select className={field} value={value.projectId ?? ''} onChange={(e) => set({ projectId: e.target.value || undefined })}>
          <option value="">Any project</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={heading}>Series</span>
        <select className={field} value={value.seriesId ?? ''} onChange={(e) => set({ seriesId: e.target.value || undefined })}>
          <option value="">Any series</option>
          {series.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={heading}>Discuss with</span>
        <select className={field} value={value.personId ?? ''} onChange={(e) => set({ personId: e.target.value || undefined })}>
          <option value="">Anyone</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <div className="flex flex-col gap-1.5">
        <span className={heading}>Date</span>
        <div className="grid grid-cols-2 gap-1.5">
          <input type="date" aria-label="From" className={field} value={value.from ?? ''} onChange={(e) => set({ from: e.target.value || undefined })} />
          <input type="date" aria-label="To" className={field} value={value.to ?? ''} onChange={(e) => set({ to: e.target.value || undefined })} />
        </div>
      </div>
      <label className="flex flex-col gap-1.5">
        <span className={heading}>Text contains</span>
        <input type="search" className={field} value={text} maxLength={200} placeholder="e.g. SSO" onChange={(e) => setText(e.target.value)} />
      </label>
      <div className="flex flex-col gap-1.5">
        <span className={heading}>Group by</span>
        <Segmented label="Group by" options={GROUPS} value={value.group} onChange={(group) => set({ group })} />
      </div>
    </div>
  );
}
